import { faArrowDown, faArrowUp } from '@fortawesome/free-solid-svg-icons';
import CircularProgress from '@material-ui/core/CircularProgress';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import LoadingComponent from 'components/LoadingComponent';
import ImageBadge from 'components/images/ImageBadge';
import { Pagination } from 'components/reports/controls/Pagination';
import { TableBody } from 'components/tables/learner-stats/TableBody';
import { TableHeader } from 'components/tables/learner-stats/TableHeader';
import { SortArrow, TableHeaderContainer, TableHeaderText } from 'components/tables/learner-stats/elements';
import { TableSearchBar } from 'components/tables/TableSearchBar';
import { Color } from 'core';
import { useCurrentUser } from 'hooks/useCurrentUser';
import axios from 'axios';
import { useQuery } from 'react-query';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';
import { ISet } from 'models/set';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { handleAddSetToCourse } from 'store/course-set';
import { formatStringForSortComparison } from 'utils/tableUtils';

interface IProps {
  contentItem: ISet;
}

export const AddContentToCourse = (props: IProps) => {
  const { contentItem } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useCurrentUser();
  const primaryPartnerId = user?.primaryPartner.id;

  // Local state for search and filtering
  const [searchName, setSearchName] = useState<string>('');

  // Pagination state
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // State for tracking button states per course
  const [courseStates, setCourseStates] = useState<{ [courseId: string]: 'idle' | 'loading' | 'success' | 'error' }>(
    {}
  );

  // Custom courses fetching function for modal
  const getCoursesForModal = async () => {
    const endpoint = `/api/v4/partners/${primaryPartnerId}/courses`;
    let queryParams = `limited=true&include=image&page[number]=${pageNumber}&page[size]=${pageSize}&status_filter=all&sort=-last_updated`;
    if (searchName) {
      queryParams += `&course_name=${searchName}`;
    }
    const response = await axios.get(`${endpoint}?${queryParams}`);
    const normalizedData = normalizeJSONAPIResponse(response.data);
    return {
      data: normalizedData,
      meta: response.data.meta,
    };
  };

  const { data, status: queryStatus } = useQuery(
    ['modal-courses', primaryPartnerId, pageNumber, pageSize, searchName],
    getCoursesForModal,
    {
      enabled: !!primaryPartnerId,
      keepPreviousData: true,
    }
  );

  const courses = data?.data || [];
  const coursesMeta = data?.meta;

  // Pagination calculations from server response
  const totalCount = coursesMeta ? coursesMeta['total-count'] : 0;
  const totalPages = coursesMeta ? coursesMeta['total-pages'] : 0;

  // Reset page when search changes
  useEffect(() => {
    setPageNumber(1);
  }, [searchName]);

  // Pagination handlers
  const goToPageOne = () => setPageNumber(1);
  const goToNextPage = () => pageNumber < totalPages && setPageNumber(pageNumber + 1);
  const goToPreviousPage = () => pageNumber > 1 && setPageNumber(pageNumber - 1);
  const goToLastPage = () => setPageNumber(totalPages);

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1);
  };

  const columns: ColumnDef<any>[] = [
    {
      id: 'name',
      accessorFn: (row) => row.name,
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <SCourseImageContainer style={{ flex: 1 }}>
              <ImageBadge image={rowData.image} style={courseImageStyles} entity="course" />
            </SCourseImageContainer>
            <CourseNameContainer>
              <CourseName as="a" href={`/app/nav/courses/${rowData.id}`} target="_blank" rel="noopener noreferrer">
                {rowData.name}
              </CourseName>
              <InstructorNames>{rowData.instructorNames?.join(', ') || t('No Instructors Yet')}</InstructorNames>
            </CourseNameContainer>
            <AddButton
              state={courseStates[rowData.id] || 'idle'}
              onClick={() => handleAddToCourse(rowData)}
              disabled={courseStates[rowData.id] === 'loading' || courseStates[rowData.id] === 'success'}
            >
              {courseStates[rowData.id] === 'loading' && <CircularProgress size={16} style={{ color: 'white' }} />}
              {courseStates[rowData.id] === 'success' && '✓ Added'}
              {courseStates[rowData.id] === 'error' && 'Retry'}
              {(!courseStates[rowData.id] || courseStates[rowData.id] === 'idle') && 'Add'}
            </AddButton>
          </div>
        );
      },
      header: () => <ColumnHeader title={t('Course')} sort={''} sortDirection={'asc'} onSort={() => {}} />,
    },
    {
      id: 'learners',
      accessorFn: (row) => row.studentUsersCount,
      cell: ({ row }) => <LearnersCell>{row.original.studentUsersCount || 0}</LearnersCell>,
      header: () => <ColumnHeader title={t('Learners')} sort={''} sortDirection={'asc'} onSort={() => {}} />,
    },
    {
      id: 'last_updated',
      accessorFn: (row) => row.updatedAt,
      cell: ({ row }) => <LastUpdatedCell>{moment(row.original.updatedAt).format('MM/DD/YY')}</LastUpdatedCell>,
      header: () => <ColumnHeader title={t('Last Updated')} sort={''} sortDirection={'asc'} onSort={() => {}} />,
    },
  ];

  const table = useReactTable({
    data: courses,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleAddToCourse = async (course: any) => {
    const courseId = course.id.toString();
    const setId = contentItem.id.toString();

    // Set loading state
    setCourseStates((prev) => ({
      ...prev,
      [courseId]: 'loading',
    }));

    try {
      await dispatch(handleAddSetToCourse(courseId, setId));

      // Set success state
      setCourseStates((prev) => ({
        ...prev,
        [courseId]: 'success',
      }));
    } catch (error) {
      console.error('Failed to add set to course:', error);

      // Set error state
      setCourseStates((prev) => ({
        ...prev,
        [courseId]: 'error',
      }));

      // Reset to idle after 3 seconds for retry
      setTimeout(() => {
        setCourseStates((prev) => ({
          ...prev,
          [courseId]: 'idle',
        }));
      }, 3000);
    }
  };

  return (
    <>
      <ToolbarContainer>
        <TableSearchBar
          value={searchName}
          onChange={setSearchName}
          resetPage={() => {}}
          placeholder={t('Search courses')}
          style={{ width: '100%' }}
        />
      </ToolbarContainer>

      {queryStatus === 'loading' ? (
        <LoadingComponent component={<></>} isLoading={true} />
      ) : (
        <TableContainer>
          <StyledTable>
            <TableHeader table={table} handleHeaderClick={() => {}} />
            <TableBody table={table} />
          </StyledTable>
          {courses.length === 0 && totalCount === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', color: Color.darkGray }}>No courses found</div>
          )}
          {totalCount > 0 && (
            <Pagination
              totalCount={totalCount}
              page={pageNumber}
              pageSize={pageSize}
              goToPageOne={goToPageOne}
              goToPreviousPage={goToPreviousPage}
              goToNextPage={goToNextPage}
              goToLastPage={goToLastPage}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </TableContainer>
      )}
    </>
  );
};

interface ColumnHeaderProps {
  title: string;
  sort: string;
  sortDirection: 'asc' | 'desc';
  onSort: () => void;
}

const ColumnHeader = ({ title, sort, sortDirection, onSort }: ColumnHeaderProps) => (
  <TableHeaderContainer
    style={Object.assign({ justifyContent: 'left', whiteSpace: 'nowrap' }, sort === '' ? {} : { cursor: 'pointer' })}
    onClick={onSort}
  >
    <TableHeaderText>{title}</TableHeaderText>
    {sort === formatStringForSortComparison(title) && (
      <SortArrow icon={sortDirection === 'desc' ? faArrowDown : faArrowUp} />
    )}
  </TableHeaderContainer>
);

const ToolbarContainer = styled.div`
  display: flex;
  border-bottom: 1px solid ${Color.lightGray};
  flex-shrink: 0;
  padding-bottom: 16px;
`;

const TableContainer = styled.div`
  flex: 1;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const SCourseImageContainer = styled.div`
  display: inline-block;
`;

const courseImageStyles = {
  height: '32px',
  width: '32px',
  verticalAlign: 'middle',
  marginRight: '16px',
};

const CourseNameContainer = styled.div`
  flex: 10;
  min-width: 0; /* Allow flex item to shrink below its content size */
`;

const CourseName = styled.div`
  font-weight: 600;
  color: ${Color.primaryBlack};
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-decoration: none;

  &:hover {
    color: ${Color.primaryBlue};
    text-decoration: underline;
  }
`;

const InstructorNames = styled.div`
  font-size: 0.8em;
  margin-top: 0.2em;
  color: ${Color.reportGray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LearnersCell = styled.span`
  display: block;
  width: 50px;
  text-align: center;
`;

const LastUpdatedCell = styled.span`
  display: block;
  width: 65px;
  text-align: center;
`;

const AddButton = styled.button<{ state: string }>`
  background: ${(props) => {
    switch (props.state) {
      case 'loading':
        return '#ccc';
      case 'success':
        return '#28a745';
      case 'error':
        return '#dc3545';
      default:
        return Color.primaryBlue;
    }
  }};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  font-size: 14px;
  font-weight: 500;
  margin-left: 16px;
  min-width: 80px;

  &:hover:not(:disabled) {
    background: ${(props) => {
      switch (props.state) {
        case 'loading':
          return '#ccc';
        case 'success':
          return '#28a745';
        case 'error':
          return '#c82333';
        default:
          return Color.primaryBlue + 'cc';
      }
    }};
  }

  &:disabled {
    opacity: ${(props) => (props.state === 'success' ? 1 : 0.7)};
  }
`;
